import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button, Spin, message } from 'antd'
import {
  ReloadOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  UndoOutlined,
} from '@ant-design/icons'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

import { getDocumentPresignedUrl } from '../../api/documents'
import './index.css'

// Use a locally served worker from /public to avoid prod issues with `.mjs` module loading.
// We copy `node_modules/pdfjs-dist/build/pdf.worker.min.mjs` into `public/pdf.worker.min.js`.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`

const PdfViewer = () => {
  const { docId } = useParams()
  const [searchParams] = useSearchParams()

  const docName = useMemo(
    () => searchParams.get('name') || 'Documento',
    [searchParams],
  )

  const [pdfUrl, setPdfUrl] = useState(null)
  const [numPages, setNumPages] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [scale, setScale] = useState(1.1)

  const MIN_SCALE = 0.6
  const MAX_SCALE = 2.6
  const STEP = 0.2

  const zoomOut = () =>
    setScale(s => Math.max(MIN_SCALE, Math.round((s - STEP) * 10) / 10))
  const zoomIn = () =>
    setScale(s => Math.min(MAX_SCALE, Math.round((s + STEP) * 10) / 10))
  const resetZoom = () => setScale(1.1)

  const load = async () => {
    setIsLoading(true)
    setError(null)
    setPdfUrl(null)
    setNumPages(null)
    try {
      const url = await getDocumentPresignedUrl(docId)
      if (!url) throw new Error('URL del PDF non disponibile')
      setPdfUrl(url)
    } catch (e) {
      setError(e?.message || 'Errore nel caricamento del PDF')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!docId) return
    load()
  }, [docId])

  useEffect(() => {
    document.body.classList.add('pdf-viewer-open')

    const onKeyDown = e => {
      const key = (e.key || '').toLowerCase()
      const isPrint = (e.ctrlKey || e.metaKey) && key === 'p'
      if (!isPrint) return
      e.preventDefault()
      e.stopPropagation()
      message.warning('Stampa disabilitata in questa pagina', 2)
    }

    window.addEventListener('keydown', onKeyDown, { capture: true })
    return () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true })
      document.body.classList.remove('pdf-viewer-open')
    }
  }, [])

  return (
    <div className="pdfViewerRoot">
      <div className="pdfViewerPrintBlock">
        <div className="pdfViewerPrintBlockInner">
          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            Stampa disabilitata
          </div>
          <div>
            Questo documento può essere visualizzato nell’app, ma non stampato
            dal browser.
          </div>
        </div>
      </div>
      <div className="pdfViewerHeader">
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <div className="pdfViewerTitle">{docName}</div>
          {numPages ? (
            <div className="pdfViewerHint">{numPages} pagine</div>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            onClick={zoomOut}
            icon={<ZoomOutOutlined />}
            disabled={scale <= MIN_SCALE}>
            Zoom -
          </Button>
          <Button
            onClick={resetZoom}
            icon={<UndoOutlined />}
            disabled={scale === 1.1}>
            {Math.round(scale * 100)}%
          </Button>
          <Button
            onClick={zoomIn}
            icon={<ZoomInOutlined />}
            disabled={scale >= MAX_SCALE}>
            Zoom +
          </Button>
          <Button onClick={load} icon={<ReloadOutlined />} disabled={isLoading}>
            Ricarica
          </Button>
        </div>
      </div>

      <div className="pdfViewerBody">
        {isLoading ? (
          <Spin size="large" />
        ) : error ? (
          <div className="pdfViewerError">
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              Impossibile caricare il PDF
            </div>
            <div style={{ marginBottom: 12 }}>{error}</div>
            <div className="pdfViewerHint">
              Nota: per renderizzare con react-pdf, il bucket S3 deve consentire
              CORS (Access-Control-Allow-Origin) verso questo dominio.
            </div>
          </div>
        ) : pdfUrl ? (
          <Document
            file={pdfUrl}
            className="pdfViewerDoc"
            loading={<Spin />}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            onLoadError={e => {
              setError(e?.message || 'Errore nel parsing del PDF')
            }}>
            {Array.from(new Array(numPages || 0), (el, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                scale={scale}
                renderAnnotationLayer
                renderTextLayer
              />
            ))}
          </Document>
        ) : null}
      </div>
    </div>
  )
}

export default PdfViewer
